import React, {
  FC,
  HTMLAttributes,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Box, Heading, Flex, Icon } from "@chakra-ui/core";
import { TitleRow } from "@engyalo/storefront-components";
import { IProductPricedPerCollectionLevel2 as productLevel2 } from "@engyalo/storefront-types/lib/graphql/product";
import { IConfig } from "@engyalo/storefront-types/lib/plugins/model";
import {
  IProductLevel2,
  IProductLevel3,
} from "@engyalo/storefront-types/lib/plugins/model";
import { CustomizationDetail } from "./CustomizationDetail";
import { ICustomizationDetail } from "./types";

interface IProps extends HTMLAttributes<HTMLDivElement> {
  customization?: productLevel2 | null;
  isLoading: boolean;
  config: IConfig;
  tFunction: (translation: string) => string;
  saveCustomization: (data: IProductLevel2) => void;
}

export const Customization: FC<IProps> = ({
  customization,
  isLoading,
  config,
  tFunction,
  saveCustomization,
}: IProps) => {
  const [selectedItems, setSelectedItems] = useState(0);
  const [level2Detail, setLevel2Detail] = useState({} as IProductLevel2);
  const [selectionReady, setSelectionReady] = useState(false);

  useEffect(() => {
    if (level2Detail) {
      const { customizations, size, minItems } = level2Detail;

      if (
        customizations &&
        size !== null &&
        size !== undefined &&
        minItems !== null &&
        minItems !== undefined
      ) {
        let customizationsSelected = 0;

        customizations.forEach((detail: IProductLevel3 | null) => {
          if (detail && detail.quantity) {
            customizationsSelected += detail.quantity;
          }
        });

        if (size !== 0) {
          setSelectionReady(customizationsSelected === size);
        } else {
          setSelectionReady(customizationsSelected > minItems);
        }
      }
    }
  }, [level2Detail]);

  useEffect(() => {
    if (selectionReady) {
      saveCustomization(level2Detail);
    }
  }, [level2Detail, saveCustomization, selectionReady]);

  const prepareCustomizationSelected = useCallback(
    (customizationsDetail: ICustomizationDetail): void => {
      const newCustomizations = Object.keys(customizationsDetail).map(
        (key) => customizationsDetail[key]
      );
      if (customization) {
        setLevel2Detail({
          ...customization,
          quantity: newCustomizations.length,
          customizations: newCustomizations,
        });
      }
    },
    [setLevel2Detail, customization]
  );

  if (customization) {
    const { id, name, description, size, maxItems } = customization;

    const getItemsSelectedText = (): string => {
      if (size && size > 0) {
        if (size > 1) {
          return ` ${selectedItems} / ${size} ${tFunction(
            "product-choose-multiple"
          )} `;
        } else {
          return `${tFunction("product-choose-option")}`;
        }
      }

      if (maxItems) {
        if (maxItems > 1) {
          return ` ${selectedItems} / ${maxItems} ${tFunction(
            "product-choose-multiple"
          )} `;
        } else {
          return `${tFunction("product-choose-option")}`;
        }
      }

      return description || "";
    };

    return (
      <Box>
        <TitleRow>
          <Flex flexDirection="column" flex="4">
            <Heading marginY="4px" as="h3" fontSize="xl" flex="1">
              {name}
            </Heading>
            <Heading marginY="4px" as="h2" fontSize="md" flex="1">
              {getItemsSelectedText()}
            </Heading>
          </Flex>
          <Flex flexDirection="column" flex="1" justifyContent="center">
            {selectionReady ? (
              <Box display="flex" justifyContent="flex-end" paddingX="1rem">
                <Icon name="check" size="1.6rem" color="green.400" />
              </Box>
            ) : (
              <>
                {size && size > 0 ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    width="7rem"
                    height="2.4rem"
                    backgroundColor="gray.400"
                    borderRadius="0.2rem"
                    justifyContent="center"
                    fontSize="1.1rem"
                    color="gray.100"
                  >
                    <span>{tFunction("product-not-optional")}</span>
                  </Box>
                ) : null}
              </>
            )}
          </Flex>
        </TitleRow>
        {customization && id && (
          <CustomizationDetail
            key={id}
            detail={customization}
            isLoading={isLoading}
            config={config}
            setSelectedItems={setSelectedItems}
            selectedItems={selectedItems}
            prepareCustomizationSelected={prepareCustomizationSelected}
          />
        )}
      </Box>
    );
  }
  return null;
};
